import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { ProvedorDeSessao, useSessao } from './autenticacao/sessao.tsx';
import { Aplicacao } from './layout/Aplicacao.tsx';
import { PaginaEmConstrucao } from './paginas/PaginaEmConstrucao.tsx';
import { PaginaAtenderAgora } from './paginas/PaginaAtenderAgora.tsx';
import { PaginaAuditoria } from './paginas/PaginaAuditoria.tsx';
import { PaginaCadencias } from './paginas/PaginaCadencias.tsx';
import { PaginaClientes } from './paginas/PaginaClientes.tsx';
import { PaginaComissoes } from './paginas/PaginaComissoes.tsx';
import { PaginaConfiguracoes } from './paginas/PaginaConfiguracoes.tsx';
import { PaginaContatos } from './paginas/PaginaContatos.tsx';
import { PaginaDashboard } from './paginas/PaginaDashboard.tsx';
import { PaginaEntrar } from './paginas/PaginaEntrar.tsx';
import { PaginaImplantacao } from './paginas/PaginaImplantacao.tsx';
import { PaginaImportarCarteira } from './paginas/PaginaImportarCarteira.tsx';
import { PaginaImportarLeads } from './paginas/PaginaImportarLeads.tsx';
import { PaginaLeads } from './paginas/PaginaLeads.tsx';
import { PaginaMetas } from './paginas/PaginaMetas.tsx';
import { PaginaMovimentacoes } from './paginas/PaginaMovimentacoes.tsx';
import { PaginaNotificacoes } from './paginas/PaginaNotificacoes.tsx';
import { PaginaPerfil } from './paginas/PaginaPerfil.tsx';
import { PaginaRenovacoes } from './paginas/PaginaRenovacoes.tsx';
import { PaginaSales } from './paginas/PaginaSales.tsx';
import { PaginaTarefas } from './paginas/PaginaTarefas.tsx';
import { PaginaVisao360 } from './paginas/PaginaVisao360.tsx';
import { PaginaUsuarias } from './paginas/PaginaUsuarias.tsx';

const clienteDeConsultas = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={clienteDeConsultas}>
      <BrowserRouter>
        <ProvedorDeSessao>
          <Rotas />
        </ProvedorDeSessao>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/**
 * A rota só decide o que mostrar. Esconder um item de menu não é controle de
 * acesso: quem garante a permissão é a API, a cada requisição.
 */
function Rotas(): React.JSX.Element {
  const { usuaria, carregando } = useSessao();

  if (carregando) {
    return (
      <main className="flex min-h-full items-center justify-center text-texto-suave">
        Carregando…
      </main>
    );
  }

  if (!usuaria) {
    return (
      <Routes>
        <Route path="/entrar" element={<PaginaEntrar />} />
        <Route path="*" element={<PaginaEntrar />} />
      </Routes>
    );
  }

  const ehAdministradora = usuaria.papel === 'ADMINISTRADORA';

  return (
    <Routes>
      <Route path="/entrar" element={<Navigate to="/" replace />} />
      <Route element={<Aplicacao />}>
        {/* O que todo papel acessa (NEX-222). Leads e Atender agora se abrem
          * de dentro do Sales, e a ficha 360 pela busca do topo — recortada, na
          * API, aos clientes de quem está logada. */}
        <Route index element={<PaginaDashboard />} />
        <Route path="sales" element={<PaginaSales />} />
        <Route path="leads" element={<PaginaLeads />} />
        <Route path="atender-agora" element={<PaginaAtenderAgora />} />
        <Route path="contatos/:contatoId" element={<PaginaVisao360 />} />
        <Route path="metas" element={<PaginaMetas />} />
        <Route path="tarefas" element={<PaginaTarefas />} />
        <Route path="perfil" element={<PaginaPerfil />} />
        {ehAdministradora ? (
          <>
            <Route path="contatos" element={<PaginaContatos />} />
            <Route path="clientes" element={<PaginaClientes />} />
            <Route path="implantacao" element={<PaginaImplantacao />} />
            <Route path="movimentacoes" element={<PaginaMovimentacoes />} />
            <Route path="renovacoes" element={<PaginaRenovacoes />} />
            <Route path="notificacoes" element={<PaginaNotificacoes />} />
            <Route path="importar-leads" element={<PaginaImportarLeads />} />
            <Route path="importar-carteira" element={<PaginaImportarCarteira />} />
            <Route path="usuarias" element={<PaginaUsuarias />} />
            <Route path="cadencias" element={<PaginaCadencias />} />
            <Route path="auditoria" element={<PaginaAuditoria />} />
            <Route path="comissoes" element={<PaginaComissoes />} />
            <Route path="configuracoes" element={<PaginaConfiguracoes />} />
          </>
        ) : null}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
